import { FC, useMemo, useRef } from 'react';

import { Box, Button, Typography, useTheme } from '@mui/material';
import { Formik } from 'formik';
import * as yup from 'yup';

import { useMutation, useQuery } from '@redwoodjs/web';

import { SEND_TO_WORLD_PAY } from 'src/graphql/mutations/sendToWorldPay.mutation';
import { GetAgreementDocuments } from 'src/graphql/queries/getAgreementDocuments.query';
import { isIreland } from 'src/lib/countryConfig';

import { Flex } from '../../../../components/Flex';
import { FieldInput } from '../../../../components/FormikComponents/FieldInput';
import { TextAreaInput } from '../../../../components/TextAreaInput';
import { showErrorToast, showSuccessToast } from '../../../../components/Toasts/toast';

import SupportingDocumentCheckbox from './SupportingDocumentCheckbox';

const RESTRICTED_DOCUMENT_NAMES = ['Card Services', 'Direct Debit'];

interface OptionTypes {
    value: string;
    label: string;
}

interface WorldPayDocumentContentProps {
    supportingDocuments: OptionTypes[];
    supportingDocumentLabels: string[];
    legalName: string;
    tradingName: string;
    onClose: () => void;
    applicationId: number;
    onSentToWorldPay: () => void;
}

export const shouldSelectApplicationDocument = (
    id: string,
    _name: string,
    applicationDocuments: string[] | undefined
): boolean => {
    return applicationDocuments?.includes(id) || false;
};

export const shouldSelectSupportingDocument = (
    value: string,
    supportingDocuments: string[] | undefined
): boolean => {
    return supportingDocuments?.includes(value) || false;
};

export const handleSupportingDocumentSelect = (
    value: string,
    label: string,
    currentValues: string[] | undefined,
    currentLabels: string[] | undefined,
    setFieldValue: (field: string, value: string[]) => void
): void => {
    const previousValues = currentValues || [];
    const previousLabels = currentLabels || [];

    if (previousValues.includes(value)) {
        setFieldValue(
            'supportingDocuments',
            previousValues.filter((el) => {
                return el !== value;
            })
        );
    } else {
        setFieldValue('supportingDocuments', [...previousValues, value]);
    }

    if (previousLabels.includes(label)) {
        setFieldValue(
            'supportingDocumentLabels',
            previousLabels.filter((el) => {
                return el !== label;
            })
        );
    } else {
        setFieldValue('supportingDocumentLabels', [...previousLabels, label]);
    }
};

export const removeRestrictedDocuments = (
    applicationDocuments: { id: string; name: string; subtext?: string }[],
    currentApplicationDocuments: string[] | undefined,
    cleanedRestrictedDocsRef: React.MutableRefObject<Set<string>>,
    setFieldValue: (field: string, value: string[]) => void
): void => {
    const restrictedDocs = applicationDocuments.filter((doc) => {
        return RESTRICTED_DOCUMENT_NAMES.some((names) => {
            return doc.name.includes(names);
        });
    });

    const docsToRemove = restrictedDocs.filter((doc) => {
        return (
            currentApplicationDocuments?.includes(doc.id) &&
            !cleanedRestrictedDocsRef.current.has(doc.id)
        );
    });

    if (docsToRemove.length > 0) {
        const idsToRemove = docsToRemove.map((doc) => {
            return doc.id;
        });
        setFieldValue(
            'applicationDocuments',
            currentApplicationDocuments?.filter((id) => {
                return !idsToRemove.includes(id);
            })
        );

        docsToRemove.forEach((doc) => {
            cleanedRestrictedDocsRef.current.add(doc.id);
        });
    }
};

const WorldPayDocumentContent: FC<WorldPayDocumentContentProps> = ({
    supportingDocuments,
    legalName,
    tradingName,
    onClose,
    applicationId,
    onSentToWorldPay,
}) => {
    const theme = useTheme();

    const cleanedRestrictedDocsRef = useRef(new Set<string>());

    const [sendToWorldPay, { loading }] = useMutation(SEND_TO_WORLD_PAY, {
        onError: () => {
            return showErrorToast('Application failed to complete');
        },
        onCompleted: () => {
            showSuccessToast('Your application has been sent to WorldPay');
            onSentToWorldPay();
        },
    });

    const { data } = useQuery(GetAgreementDocuments, {
        onError: (err) => {
            return showErrorToast(err.message);
        },
        variables: { applicationId },
    });

    const applicationDocuments: { id: string; name: string; subtext?: string }[] = useMemo(() => {
        const documents = data?.agreementDocuments || [];
        return documents.map(({ id, name }) => {
            if (name === 'Base_WorldPayForms') {
                return {
                    id,
                    name: 'WorldPay Forms',
                    subtext: 'Includes: WorldPay Application, WorldPay DD Mandate',
                };
            }
            return {
                id,
                name,
                subtext: '',
            };
        });
    }, [data]);

    const defaultApplicationDocs = useMemo(() => {
        return (
            data?.agreementDocuments
                ?.filter(({ name }) => {
                    return (
                        name !== 'NPI Card Services Agreement - v2' &&
                        name !== 'NPI_Direct_Debit_Mandate_V2'
                    );
                })
                .map(({ id }) => {
                    return id;
                }) || []
        );
    }, [data]);

    const allSupportingDocuments = useMemo(() => {
        return supportingDocuments.map(({ value, label }) => {
            return { value, label };
        });
    }, [supportingDocuments]);

    return (
        <Formik
            initialValues={{
                emailSubject: `Merchant Application (${applicationId}) - (${legalName} T/A ${tradingName})`,
                emailMessage:
                    'Dear WorldPay, \n\nPlease find application attached.\n\nPlease acknowledge receipt of this email.\n\nRegards,\nNew Payment Innovation Limited',
                supportingDocuments: !isIreland
                    ? []
                    : allSupportingDocuments.map(({ value }) => {
                          return value;
                      }),
                supportingDocumentLabels: !isIreland
                    ? []
                    : allSupportingDocuments.map(({ label }) => {
                          return label;
                      }),
                applicationDocuments: defaultApplicationDocs,
                applicationId,
            }}
            enableReinitialize
            onSubmit={async (input) => {
                await sendToWorldPay({
                    variables: { input: { ...input, applicationId } },
                });
            }}
            validationSchema={yup.object().shape({
                emailSubject: yup.string().required('Attach an email subject'),
                emailMessage: yup.string().required('Attach an email message'),
                supportingDocuments: yup.array().of(yup.string()),
                applicationDocuments: yup
                    .array()
                    .of(yup.string())
                    .min(1, 'Please add application documents')
                    .required('Please add application documents'),
                applicationId: yup.number().required('No application Id has been attached'),
            })}
        >
            {({ values, setFieldValue, isSubmitting, isValid, handleSubmit }) => {
                const shouldDisableSendButton = isSubmitting || loading || !isValid;

                removeRestrictedDocuments(
                    applicationDocuments,
                    values.applicationDocuments,
                    cleanedRestrictedDocsRef,
                    setFieldValue
                );

                return (
                    <Box borderRadius="16px" bgcolor="#fff" overflow="hidden">
                        <Flex flexDirection="column" padding="24px" color="#1d1d1b">
                            <Box>
                                <Typography
                                    fontWeight="600"
                                    fontSize="20px"
                                    lineHeight="30px"
                                    marginBottom="8px"
                                >
                                    Ready to send to WorldPay?
                                </Typography>
                                <Typography
                                    fontWeight="500"
                                    fontSize="18px"
                                    lineHeight="27px"
                                    marginTop="8px"
                                >
                                    Please select ALL documents that need to be sent to WorldPay
                                </Typography>
                                <Flex marginTop="24px" gap="64px">
                                    <Flex flex={1} flexDirection="column">
                                        <Typography
                                            fontWeight="600"
                                            fontSize="20px"
                                            lineHeight="30px"
                                        >
                                            Application Documents
                                        </Typography>
                                        {applicationDocuments.map(({ id, name, subtext }) => {
                                            return (
                                                <SupportingDocumentCheckbox
                                                    key={id}
                                                    label={name.replace(/_/g, ' ')}
                                                    isSelected={shouldSelectApplicationDocument(
                                                        id,
                                                        name,
                                                        values.applicationDocuments
                                                    )}
                                                    subtext={subtext}
                                                    onSelect={() => {
                                                        const prev =
                                                            values.applicationDocuments || [];
                                                        if (prev.includes(id)) {
                                                            setFieldValue(
                                                                'applicationDocuments',
                                                                prev.filter((el) => {
                                                                    return el !== id;
                                                                })
                                                            );
                                                        } else {
                                                            setFieldValue('applicationDocuments', [
                                                                ...prev,
                                                                id,
                                                            ]);
                                                        }
                                                    }}
                                                />
                                            );
                                        })}
                                    </Flex>
                                    <Flex flex={1} flexDirection="column">
                                        <Typography
                                            fontWeight="600"
                                            fontSize="20px"
                                            lineHeight="30px"
                                        >
                                            Supporting Documents
                                        </Typography>
                                        {supportingDocuments.map(({ label, value }) => {
                                            return (
                                                <SupportingDocumentCheckbox
                                                    key={value}
                                                    label={label.replace(/_/g, ' ')}
                                                    isSelected={shouldSelectSupportingDocument(
                                                        value,
                                                        values.supportingDocuments
                                                    )}
                                                    subtext=""
                                                    onSelect={() => {
                                                        return handleSupportingDocumentSelect(
                                                            value,
                                                            label,
                                                            values.supportingDocuments,
                                                            values.supportingDocumentLabels,
                                                            setFieldValue
                                                        );
                                                    }}
                                                />
                                            );
                                        })}
                                    </Flex>
                                </Flex>
                                <Box marginTop="48px">
                                    <FieldInput
                                        name="emailSubject"
                                        disabled
                                        label="Email Subject"
                                        boxStyling={{ marginBottom: '24px' }}
                                    />
                                    <TextAreaInput
                                        name="emailMessage"
                                        placeholder="Enter your message"
                                        disabled
                                        label="Message"
                                    />
                                </Box>
                            </Box>
                            <Box display="flex" justifyContent="flex-end" marginTop="24px">
                                <Button
                                    variant="main"
                                    sx={{ marginRight: '16px', bgcolor: `${theme.palette.grey}` }}
                                    onClick={onClose}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="main"
                                    sx={{
                                        width: 'max-content',
                                        bgcolor: shouldDisableSendButton
                                            ? `${theme.palette.grey}`
                                            : `${theme.palette.dark}`,
                                    }}
                                    style={{ color: 'white' }}
                                    onClick={() => {
                                        if (values?.supportingDocuments.length === 0 && isIreland) {
                                            showErrorToast('Supporting documents are required!');
                                        } else {
                                            handleSubmit();
                                        }
                                    }}
                                    disabled={shouldDisableSendButton}
                                >
                                    {loading ? 'Sending to WorldPay...' : 'Send to WorldPay'}
                                </Button>
                            </Box>
                        </Flex>
                    </Box>
                );
            }}
        </Formik>
    );
};

export default WorldPayDocumentContent;
